export function sortRows(rows) {
  return [...(rows || [])].sort((a, b) => {
    const aSort = Number(a.sort) || 0
    const bSort = Number(b.sort) || 0
    return aSort - bSort
  })
}

export function normalizeParentId(value) {
  if (value === null || value === undefined || value === "") return null
  return String(value)
}

export function getStoragePathFromUrl(url) {
  if (!url) return null
  const cleanUrl = url.split("?")[0]
  const marker = "/storage/v1/object/"
  const idx = cleanUrl.indexOf(marker)
  if (idx === -1) return null

  let path = cleanUrl.slice(idx + marker.length)
  if (path.startsWith("public/")) {
    path = path.slice("public/".length)
  }

  const decoded = decodeURIComponent(path)
  const [bucket, ...rest] = decoded.split("/")
  if (!bucket || !rest.length) return null
  return { bucket, path: rest.join("/") }
}

export async function deleteStorageByUrl(supabase, url) {
  const info = getStoragePathFromUrl(url)
  if (!info) return { error: null }
  const { error } = await supabase.storage.from(info.bucket).remove([info.path])
  return { error: error || null }
}

export function buildCategoryPathMap(categories) {
  const rows = categories || []
  const byParent = rows.reduce((acc, category) => {
    const key = normalizeParentId(category.parent_id) || "__root__"
    if (!acc[key]) acc[key] = []
    acc[key].push(category)
    return acc
  }, {})

  Object.keys(byParent).forEach(key => {
    byParent[key] = sortRows(byParent[key])
  })

  const pathMap = {}

  function walk(parentId, chain) {
    const key = normalizeParentId(parentId) || "__root__"
    const children = byParent[key] || []
    children.forEach(category => {
      const nextChain = [...chain, category.title]
      pathMap[category.id] = nextChain.join(" / ")
      walk(category.id, nextChain)
    })
  }

  walk(null, [])
  return pathMap
}

export function flattenCategoriesWithDepth(categories) {
  const rows = categories || []
  const byParent = rows.reduce((acc, category) => {
    const key = normalizeParentId(category.parent_id) || "__root__"
    if (!acc[key]) acc[key] = []
    acc[key].push(category)
    return acc
  }, {})

  Object.keys(byParent).forEach(key => {
    byParent[key] = sortRows(byParent[key])
  })

  const flattened = []

  function walk(parentId, depth) {
    const key = normalizeParentId(parentId) || "__root__"
    const children = byParent[key] || []
    children.forEach(category => {
      flattened.push({
        ...category,
        depth,
      })
      walk(category.id, depth + 1)
    })
  }

  walk(null, 0)
  return flattened
}

function collectCategoryBranch(categories, rootId) {
  const normalizedRootId = String(rootId)
  const byParent = (categories || []).reduce((acc, category) => {
    const key = normalizeParentId(category.parent_id) || "__root__"
    if (!acc[key]) acc[key] = []
    acc[key].push(category)
    return acc
  }, {})

  const branch = []

  function walk(categoryId, depth) {
    const match = (categories || []).find(category => String(category.id) === String(categoryId))
    if (!match) return
    branch.push({ ...match, depth })
    const key = normalizeParentId(categoryId) || "__root__"
    const children = byParent[key] || []
    children.forEach(child => walk(child.id, depth + 1))
  }

  walk(normalizedRootId, 0)
  return branch
}

export async function deleteCategoryBranch(supabase, categoryId) {
  const { data: allCategories, error: categoryFetchError } = await supabase
    .from("categories")
    .select("id,parent_id,cover")

  if (categoryFetchError) {
    return { error: "Kategori agaci alinamadi." }
  }

  const branch = collectCategoryBranch(allCategories || [], categoryId)
  if (!branch.length) {
    return { error: "Kategori bulunamadi." }
  }

  const categoryIds = branch.map(category => category.id)
  const categoryCovers = branch
    .map(category => category.cover)
    .filter(Boolean)

  const { data: subcategories, error: subcategoryFetchError } = await supabase
    .from("subcategories")
    .select("id,category_id")
    .in("category_id", categoryIds)

  if (subcategoryFetchError) {
    return { error: "Sub kategoriler alinamadi." }
  }

  const subIds = (subcategories || []).map(subcategory => subcategory.id)
  if (subIds.length) {
    const { data: items, error: itemFetchError } = await supabase
      .from("items")
      .select("id,image")
      .in("subcategory_id", subIds)

    if (itemFetchError) {
      return { error: "Urunler alinamadi." }
    }

    for (const item of items || []) {
      await deleteStorageByUrl(supabase, item.image)
    }

    const { error: deleteItemsError } = await supabase
      .from("items")
      .delete()
      .in("subcategory_id", subIds)

    if (deleteItemsError) {
      return { error: "Urunler silinemedi." }
    }

    const { error: deleteSubcategoriesError } = await supabase
      .from("subcategories")
      .delete()
      .in("id", subIds)

    if (deleteSubcategoriesError) {
      return { error: "Sub kategoriler silinemedi." }
    }
  }

  for (const cover of categoryCovers) {
    await deleteStorageByUrl(supabase, cover)
  }

  const deleteOrder = [...branch].sort((a, b) => b.depth - a.depth)
  for (const category of deleteOrder) {
    const { error: deleteCategoryError } = await supabase
      .from("categories")
      .delete()
      .eq("id", category.id)

    if (deleteCategoryError) {
      return { error: "Kategori silinemedi." }
    }
  }

  return { error: null }
}
